<script setup lang="ts">
import { toRefs, computed } from 'vue';
import { addImageScaler, formatPrice } from '@/core';
import type { Elevation } from '@/types/elevations';
import LazyImage from './LazyImage.vue';
import ExteriorCardSkeleton from './ExteriorCardSkeleton.vue';

const props = defineProps<{
  elevations: Elevation[];
  loading: boolean;
  error: string | null;
  selected: number | null;
  planName: string;
  hasAvailableLots: boolean;
  planPriceLowWithPremium?: number;
}>();
const {
  elevations,
  loading,
  error,
  selected,
  planName,
  hasAvailableLots,
  planPriceLowWithPremium,
} = toRefs(props);
const emit = defineEmits(['update:selected']);

// Mirror the sidebar plan card (SideBarPlanListItem.vue), which shows the price only when
// the plan-level `price_low_with_premium` is > 0 (its `>= 0` render gate combined with the
// truthy check on the price). This is the same plan object the sidebar uses, so a plan the
// sidebar marks "Price not available at this time" hides the price here too. Individual
// elevations are gated separately on their own `price` in the template.
const planHasPrice = computed(() => (planPriceLowWithPremium.value ?? 0) > 0);
</script>

<template>
  <div>
    <div v-if="loading">
      <ExteriorCardSkeleton />
    </div>
    <div v-else-if="error">{{ error }}</div>
    <div v-else-if="elevations.length === 0">No elevations available.</div>

    <div
      v-else
      v-for="(elevation, idx) in elevations"
      :key="elevation.elevation"
      class="plan-card"
      :class="{ selected: selected === idx }"
      @click="$emit('update:selected', idx)"
    >
      <LazyImage
        :src="addImageScaler(elevation.images[0]?.asset_value ?? '')"
        :alt="elevation.images[0]?.alt_tag || planName"
        :isNeighborhoodImage="false"
      />
      <div class="plan-info">
        <h3>{{ planName }} {{ elevation.elevation }}</h3>
        <p v-if="hasAvailableLots && planHasPrice && elevation.price > 0">
          <span class="plan-info-label">Starts at </span>
          <span>{{ formatPrice(elevation.price) }}</span>
        </p>
        <p v-else>
          <span class="plan-info-label">Price not available at this time</span>
        </p>
      </div>
    </div>
  </div>
</template>
